import React, { useState, useEffect } from 'react';
import { ref, get, set, update, remove } from 'firebase/database';
import { database } from '../firebase';

const RTDBViewerPanel = () => {
    const [selectedNode, setSelectedNode] = useState('licenses');

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [expandedPaths, setExpandedPaths] = useState(new Set());

    const [editPath, setEditPath] = useState('');
    const [editKey, setEditKey] = useState('');
    const [editValue, setEditValue] = useState('');
    const [editMsg, setEditMsg] = useState('');
    const [saving, setSaving] = useState(false);

    const nodes = [

        { id: 'licenses', label: 'Licencias', icon: '🔑' },
        { id: 'licencesPremium', label: 'Licencias Premium', icon: '💎' },
        { id: 'devices', label: 'Dispositivos', icon: '📱' },
        { id: 'payment_config', label: 'Config. Pagos', icon: '⚙️' },
        { id: 'security_logs', label: 'Logs Seguridad', icon: '🛡️' },
        { id: 'payments', label: 'Pagos Realizados', icon: '💳' },
        { id: 'appConfig', label: 'Config. App', icon: '📱' },
        { id: 'admins', label: 'Administradores', icon: '👥' },
        { id: 'users', label: 'Usuarios', icon: '👥' },
    ];

    useEffect(() => {
        fetchData();
    }, [selectedNode]);

    const fetchData = async () => {
        setLoading(true);
        setData(null);
        try {
            const snapshot = await get(ref(database, selectedNode));
            if (snapshot.exists()) {
                setData(snapshot.val());
            } else {
                setData({ message: 'No hay datos en este nodo.' });
            }
        } catch (err) {
            setData({ error: err.message });
        } finally {
            setLoading(false);
        }
    };

    const toggleExpand = (path) => {
        const next = new Set(expandedPaths);
        if (next.has(path)) next.delete(path);
        else next.add(path);
        setExpandedPaths(next);
    };

    // ---- Gestión RTDB: agregar / editar campos, guardar y eliminar nodos ----
    const applyEdits = () => {
        const snapshot = get(ref(database, selectedNode));
        // placeholder; reemplazado abajo
    };

    const readValueAtPath = async (path) => {
        try {
            const snapshot = await get(ref(database, path));
            return snapshot.exists() ? snapshot.val() : undefined;
        } catch (err) {
            setEditMsg('Error leyendo: ' + err.message);
            return undefined;
        }
    };

    const parseEditValue = (raw) => {
        const v = String(raw || '').trim();
        if (v === '') return '';
        if (v === 'true') return true;
        if (v === 'false') return false;
        if (v === 'null') return null;
        if (/^-?\d+(\.\d+)?$/.test(v)) return Number(v);
        try { return JSON.parse(v); } catch (e) { return v; }
    };

    const saveField = async () => {
        const field = editKey.trim();
        if (!field) { setEditMsg('Escribe el nombre del campo (editKey).'); return; }
        if (!selectedNode) { setEditMsg('Selecciona una raíz antes de guardar.'); return; }
        setSaving(true); setEditMsg('');
        try {
            const pathBase = editPath ? editPath.replace(/^\/+|^models\//, '') : selectedNode;
            const target = editPath && !editPath.startsWith(selectedNode) ? `${selectedNode}/${editPath}` : pathBase;
            const cleaned = target.replace(/^models\//, '');
            await update(ref(database, cleaned), { [field]: parseEditValue(editValue) });
            setEditMsg(`Campo "${field}" guardado en /${cleaned}.`);
            setEditKey(''); setEditValue(''); setEditPath('');
            await fetchData();
        } catch (err) {
            setEditMsg('Error guardando: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const saveNode = async () => {
        if (!selectedNode) { setEditMsg('Selecciona una raíz.'); return; }
        setSaving(true); setEditMsg('');
        try {
            const target = editPath ? `${selectedNode}${editPath.startsWith('/') ? editPath : '/' + editPath}` : selectedNode;
            await set(ref(database, target.replace(/^models\//, '')), parseEditValue(editValue));
            setEditMsg(`Nodo guardado en /${target.replace(/^models\//, '')}.`);
            setEditValue(''); setEditPath('');
            await fetchData();
        } catch (err) {
            setEditMsg('Error guardando: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const deleteField = async () => {
        const field = editKey.trim();
        if (!field) { setEditMsg('Escribe el nombre del campo a eliminar.'); return; }
        if (!window.confirm(`¿Eliminar el campo "${field}" de /${selectedNode}?`)) return;
        setSaving(true); setEditMsg('');
        try {
            await update(ref(database, selectedNode.replace(/^models\//, '')), { [field]: null });
            setEditMsg(`Campo "${field}" eliminado.`);
            setEditKey('');
            await fetchData();
        } catch (err) {
            setEditMsg('Error eliminando: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const deleteCurrentNode = async () => {
        if (!selectedNode) { setEditMsg('Selecciona una raíz.'); return; }
        const target = editPath ? `${selectedNode}/${editPath}` : selectedNode;
        if (!window.confirm(`¿ELIMINAR el nodo completo /${target.replace(/^models\//, '')}? Esta acción es irreversible.`)) return;
        setSaving(true); setEditMsg('');
        try {
            await remove(ref(database, target.replace(/^models\//, '')));
            setEditMsg(`Nodo eliminado: /${target.replace(/^models\//, '')}.`);
            setEditPath('');
            await fetchData();
        } catch (err) {
            setEditMsg('Error eliminando: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const renderValue = (value, path = '') => {
        if (value === null) return <span className="null-val">null</span>;
        if (typeof value === 'boolean') return <span className={`bool-val ${value}`}>{value.toString()}</span>;
        if (typeof value === 'number') return <span className="num-val">{value}</span>;
        if (typeof value === 'string') return <span className="str-val">"{value}"</span>;

        const isExpanded = expandedPaths.has(path);
        const isArray = Array.isArray(value);
        const entries = Object.entries(value);

        return (
            <div className="json-container">
                <span className="toggle-icon" onClick={() => toggleExpand(path)}>
                    {isExpanded ? '▼' : '▶'} {isArray ? `[${value.length}]` : '{...}'}
                </span>
                {isExpanded && (
                    <div className="json-content">
                        {entries.map(([key, val]) => (
                            <div key={key} className="json-row">
                                <span className="json-key">{key}:</span>
                                {renderValue(val, `${path}/${key}`)}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="admin-panel-card rtdb-explorer">
            <div className="explorer-header">
                <h3>🗄️ RTDB Explorer</h3>
                <div className="node-selector">
                    {nodes.map(node => (
                        <button
                            key={node.id}
                            className={selectedNode === node.id ? 'active' : ''}
                            onClick={() => setSelectedNode(node.id)}
                        >
                            {node.icon} {node.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="explorer-body">
                {loading ? (
                    <div className="loading-spinner"></div>
                ) : (
                    <div className="json-root">
                        <strong>Root: /{selectedNode}</strong>
                        {data && renderValue(data, 'root')}
                    </div>
                )}
            </div>

            <style>{`
                .rtdb-explorer { display: flex; flex-direction: column; gap: 20px; }
                .node-selector { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 20px; }
                .node-selector button { padding: 8px 15px; border-radius: 20px; border: 1px solid #ccc; background: white; cursor: pointer; transition: all 0.3s; }
                .node-selector button.active { background: var(--admin-btn-custom); color: white; border-color: var(--admin-btn-custom); }
                .json-root { background: #f8f9fa; padding: 20px; border-radius: 8px; font-family: 'Courier New', Courier, monospace; overflow-x: auto; color: #333; }
                .json-content { padding-left: 20px; border-left: 1px dashed #ccc; margin-left: 5px; }
                .json-row { margin: 5px 0; display: flex; flex-wrap: wrap; gap: 5px; }
                .json-key { color: #881391; font-weight: bold; }
                .toggle-icon { cursor: pointer; color: #0056b3; font-weight: bold; }
                .str-val { color: #ca1243; }
                .num-val { color: #1c00cf; }
                .bool-val.true { color: #0d904f; }
                .bool-val.false { color: #d93025; }
                .null-val { color: #808080; font-style: italic; }
            `}</style>
        </div>
    );
};

export default RTDBViewerPanel;
